import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { type FileType } from "~/api/types";
import { CircleAlert, FileUp } from "lucide-react";
import { useState } from "react";
import { cn } from "~/lib/utils";
import { useTranslation } from "react-i18next";
import { useDropZone } from "~/hooks/useDropZone";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/atoms/form";
import { UPLOAD_FILE_TYPES_ACCEPT } from "~/api/v2/constants";
import { FileListItem } from "~/molecules/FileListItem";
import {
  formatFileSize,
  getExpectedExtension,
  validateFile,
} from "~/utils/file-validation";

const UploadFormSchema = z.object({
  file: z.file(),
});

type UploadState = "initial" | "selected" | "uploading";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionTitle: string;
  currentFileType: FileType;
  onUpload: (file: File) => Promise<void>;
}

export const UploadFileModal = ({
  open,
  onOpenChange,
  versionTitle,
  currentFileType,
  onUpload,
}: Props) => {
  const { t } = useTranslation();
  const [uploadState, setUploadState] = useState<UploadState>("initial");

  const form = useForm({
    resolver: zodResolver(UploadFormSchema),
  });

  const selectedFile = useWatch({
    control: form.control,
    name: "file",
  });

  const handleFileChange = (file: File | undefined) => {
    if (!file) {
      setUploadState("initial");
      return;
    }

    form.clearErrors("file");
    form.setValue("file", file);
    setUploadState("selected");
  };

  const handleRemoveFile = () => {
    form.setValue("file", undefined as unknown as File);
    setUploadState("initial");
  };

  const handleClose = () => {
    handleRemoveFile();
    form.reset();
    onOpenChange(false);
  };

  const { isDragging, dropZoneProps } = useDropZone({
    onDrop: (files) => {
      const file = files[0];
      if (file) handleFileChange(file);
    },
  });

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    const expectedExt = getExpectedExtension(currentFileType);
    const validationError = validateFile(selectedFile, [expectedExt]);

    if (validationError === "file_too_large") {
      form.setError("file", {
        type: "manual",
        message: t(($) => $.commons.document.fileSizeLimit),
      });
      return;
    }

    if (validationError === "unsupported_type" && currentFileType === "sfdt") {
      form.setError("file", {
        type: "manual",
        message: t(($) => $.commons.workflow.cannotUploadSfdtFile, {
          fileName: selectedFile.name,
        }),
      });
      return;
    }

    if (validationError === "unsupported_type") {
      form.setError("file", {
        type: "manual",
        message: t(($) => $.commons.workflow.cannotUploadFileWrongFormat, {
          fileName: selectedFile.name,
          currentFileType: currentFileType.toUpperCase(),
          allowedExtension: expectedExt,
        }),
      });
      return;
    }

    try {
      setUploadState("uploading");
      await onUpload(selectedFile);
      handleClose();
    } catch {
      setUploadState("selected");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md [&>button]:hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {t(($) => $.commons.workflow.uploadFileTitle)}
            </DialogTitle>
          </div>
          <DialogDescription className="texm-sm text-left">
            {t(($) => $.commons.workflow.replaceFileDescription, {
              versionTitle,
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground text-xs">
                {t(($) => $.commons.workflow.fileToReplace)}
              </p>
              <FileListItem
                name={versionTitle}
                subtitle={`Current file type: ${currentFileType.toUpperCase()}${currentFileType === "sfdt" ? " (accepts .docx)" : ""}`}
              />
            </div>

            <div className="border-destructive/30 bg-destructive/10 text-destructive flex gap-4 rounded-lg border p-4">
              <div className="pt-1">
                <CircleAlert className="size-4" />
              </div>
              <span className="text-sm font-extralight">
                {t(($) => $.commons.workflow.replaceWarning)}
              </span>
            </div>

            <FormField
              control={form.control}
              name="file"
              render={({
                field: { value: _value, onChange, ...fieldProps },
              }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...fieldProps}
                      id="file-input"
                      type="file"
                      accept={UPLOAD_FILE_TYPES_ACCEPT}
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        onChange(file);
                        handleFileChange(file);
                      }}
                    />
                  </FormControl>
                  <div
                    className={cn(
                      "border-border cursor-pointer rounded-lg border-2 border-dashed",
                      isDragging ? "bg-muted/50" : "hover:bg-muted/50",
                      selectedFile ? "p-4" : "p-8",
                    )}
                    onClick={() =>
                      document.getElementById("file-input")?.click()
                    }
                    {...dropZoneProps}
                  >
                    {selectedFile ? (
                      <div className="flex flex-col gap-3">
                        <FileListItem
                          name={selectedFile.name}
                          subtitle={formatFileSize(selectedFile.size)}
                        />
                        <p className="text-muted-foreground text-xs">
                          {t(($) => $.commons.workflow.clickToSelectDifferent)}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-center">
                        <FileUp className="text-muted-foreground size-8" />
                        <p className="text-md">
                          {t(($) => $.commons.workflow.clickOrDragToUpload)}
                        </p>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="secondary" onClick={handleClose}>
            {t(($) => $.commons.workflow.cancel)}
          </Button>
          <Button
            onClick={
              uploadState === "initial"
                ? () => document.getElementById("file-input")?.click()
                : handleUploadClick
            }
            disabled={uploadState === "uploading"}
          >
            {uploadState === "uploading" ? (
              <>
                <span className="mr-2">Uploading...</span>
                <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              </>
            ) : uploadState === "selected" ? (
              t(($) => $.commons.buttons.confirmUpload)
            ) : (
              t(($) => $.dashboard.createDocument.uploadFile)
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
